'use client';

import type { McpServerResponseDto } from '@terra/shared-contracts-mcp-servers';
import { getMcpServerDisplayName } from '@terra/shared-utils';
import { CableIcon, ShieldEnergyIcon } from '@terra/ui-icons';
import { Box, HStack, VStack } from '@terra/ui-styled-system';
import { Badge } from '../../components/badge';
import { Switch } from '../../components/switch';
import {
  BodyDefault,
  BodyStrong,
  MetaDefault,
} from '../../components/typography';

export type McpServersAttachListLabels = {
  loading: string;
  emptyTitle: string;
  emptyDescription: string;
  noEligibleTitle: string;
  noEligibleDescription: string;
  requiresApproval: string;
};

export type McpServersAttachListProps = {
  servers: McpServerResponseDto[];
  attachedServerIds: ReadonlySet<string>;
  hasTenantServers: boolean;
  isLoading: boolean;
  isMutating: boolean;
  labels: McpServersAttachListLabels;
  onToggle: (input: { server: McpServerResponseDto; checked: boolean }) => void;
};

export function McpServersAttachList({
  servers,
  attachedServerIds,
  hasTenantServers,
  isLoading,
  isMutating,
  labels,
  onToggle,
}: McpServersAttachListProps) {
  if (isLoading) {
    return (
      <VStack gap="2" alignItems="center" justifyContent="center" py="6">
        <BodyDefault color="fg.muted">{labels.loading}</BodyDefault>
      </VStack>
    );
  }

  if (servers.length === 0) {
    return (
      <VStack gap="4" alignItems="center" justifyContent="center" py="6">
        <CableIcon size={40} />
        <VStack gap="1" alignItems="center">
          <BodyStrong color="fg.default">
            {hasTenantServers ? labels.noEligibleTitle : labels.emptyTitle}
          </BodyStrong>
          <MetaDefault color="fg.muted" textAlign="center" maxW="320px">
            {hasTenantServers
              ? labels.noEligibleDescription
              : labels.emptyDescription}
          </MetaDefault>
        </VStack>
      </VStack>
    );
  }

  return (
    <VStack gap="2" alignItems="stretch" w="full">
      {servers.map((server) => {
        const isAttached = attachedServerIds.has(server.id);

        return (
          <Box
            key={server.id}
            p="4"
            borderWidth="1px"
            borderColor={isAttached ? 'border.default' : 'border.subtle'}
            borderRadius="md"
            bg={isAttached ? 'bg.subtle' : 'bg.default'}
            w="full"
          >
            <HStack justify="space-between" w="full" alignItems="center">
              <HStack gap="3" alignItems="center" minW="0" flex="1">
                <CableIcon size={16} />
                <VStack gap="0.5" alignItems="flex-start" minW="0">
                  <HStack gap="2" alignItems="center">
                    <BodyStrong color="fg.default">
                      {getMcpServerDisplayName(server)}
                    </BodyStrong>
                    {server.requiresApproval ? (
                      <Badge
                        variant="outline"
                        colorScheme="orange"
                        icon={<ShieldEnergyIcon size={11} />}
                      >
                        {labels.requiresApproval}
                      </Badge>
                    ) : null}
                  </HStack>
                  <MetaDefault color="fg.muted" truncate maxW="340px">
                    {server.url ?? ''}
                  </MetaDefault>
                </VStack>
              </HStack>
              <Switch
                checked={isAttached}
                onCheckedChange={(details) =>
                  onToggle({ server, checked: details.checked })
                }
                disabled={isMutating}
                size="md"
                flexShrink={0}
              />
            </HStack>
          </Box>
        );
      })}
    </VStack>
  );
}
